import React from 'react';
import { DetailCardType } from 'types/DetailPageTypes';
import { TabType } from 'types/TabTypes';
import DetailPage from 'components/layout/DetailPage';
import { HeaderButtons } from 'types/ButtonTypes';
import AssetForm from '../../views/assets/AssetForm';
import { useActiveFeature } from 'services/permissions/permissionChecks';

// Rename the name of the function:
const ContractDetail: React.FC = () => {

    // Specify the parameters of this detail page:
    const apiObject = 'contract';
    const pageObject = 'contract';
    const headerTitleDataField = 'header_title';

    // Define the header buttons:
    const headerButtons: HeaderButtons = [
        {
            type: 'secondary',
            label: 'contract.general.cancel_button_label',
            action: 'contract_cancel',
            restrictedRight: ['only_view'],
            condition: [{ field: 'status', values: ['active'] }],
            size: 'medium'
        },
    ];

    // Only show the invoices tab when the invoices module is active
    const invoicesActive = useActiveFeature('invoices');

    // Define the detail page tabs, the timeline lives inside the work tab
    const tabs: TabType[] = [
        { label: 'contract.general.job_schedules_tab_label', type: 'contract-job-schedules' },
        ...(invoicesActive ? [{ label: 'contract.general.invoices_tab_label', type: 'contract-invoices' }] : []),
        { label: 'document.general.documents_label', type: 'attachments' },
    ];

    // Define detail components and add fields to FieldGroup cards:
    const detailCards: DetailCardType[] = [
        {
            component: 'FieldsCard',
            title: 'contract.general.details_card_label',
            alignment: 'horizontal',
            fields: [
                {
                    type: 'text',
                    name: 'number',
                    label: 'contract.general.number_label',
                    placeholder: 'contract.general.number_label',
                    isEditable: { editability: false },
                },
                {
                    type: 'text',
                    name: 'reference',
                    label: 'contract.general.reference_label',
                    placeholder: 'contract.general.reference_placeholder',
                },
                {
                    type: 'date',
                    name: 'start_date',
                    label: 'contract.general.start_date_label',
                },
                {
                    type: 'date',
                    name: 'end_date',
                    label: 'contract.general.end_date_label',
                    helperText: 'contract.general.end_date_helper',
                },
                {
                    type: 'dropdown',
                    name: 'division',
                    label: 'contract.general.division_label',
                    disabled_selected: 'contract.general.division_disabled_selected',
                    apiRequest: [{
                        endpoint: 'get_division_list',
                        object: 'division',
                        params: { is_active: true },
                        useCache: true
                    }],
                    allowNoneOption: false,
                    showSearch: false,
                    activeFeature: 'divisions',
                },
            ]
        },
        {
            component: 'FieldsCard',
            title: 'contract.general.client_card_label',
            alignment: 'horizontal',
            fields: [
                {
                    type: 'searchselect',
                    name: 'client',
                    label: 'contract.general.client_label',
                    placeholder: 'contract.general.client_placeholder',
                    objectName: 'client.general.object_name.singular',
                    query: {
                        endpoint: 'get_contact_search',
                        params: {
                            contact_type: ['contact', 'lead', 'client']
                        }
                    },
                    viewModeLinkUrl: { function: (data) => data?.entity_type === 'person' ? `/contacts/person/${data.id}` : `/contacts/organization/${data.id}` },
                    selectionFormat: 'name',
                    optionFormat: { title: { field: 'name', format: (value) => value } },
                    disableAddNew: true,
                },
                {
                    type: 'searchselect',
                    name: 'work_location',
                    label: 'contract.general.work_location_label',
                    placeholder: 'contract.general.work_location_placeholder',
                    objectName: 'location.general.object_name.singular',
                    query: { endpoint: 'get_location_search' },
                    showOptionsOnClick: (updatedData) => {
                        if (!updatedData.client?.id) return null;
                        return { endpoint: `get_location_search/${updatedData.client.id}` };
                    },
                    viewModeLinkUrl: { baseUrl: '/contacts/work-locations' },
                    selectionFormat: 'address',
                    optionFormat: { title: { field: 'address', format: (value) => value } },
                    disableAddNew: true,
                    directOptionsTitle: 'job.general.locations_of_client_label',
                },
                {
                    type: 'multi-searchselect',
                    name: 'assets',
                    label: 'contract.general.assets_label',
                    placeholder: 'contract.general.assets_placeholder',
                    tooltipText: 'contract.general.assets_tooltip',
                    objectName: 'asset.general.object_name.singular',
                    activeFeature: 'asset_management',
                    query: (updatedData) => {
                        if (updatedData?.work_location?.id) {
                            return { endpoint: `get_asset_search/${updatedData.work_location.id}` };
                        }
                        return { endpoint: 'get_asset_search' };
                    },
                    showOptionsOnClick: (updatedData) => {
                        if (!updatedData.work_location?.id) return null;
                        return { endpoint: `get_asset_search/${updatedData.work_location.id}` };
                    },
                    directOptionsTitle: 'asset.general.assets_of_location_label',
                    selectionFormat: 'name',
                    optionFormat: {
                        title: { field: 'name', format: (value) => value },
                        subtitle: { field: 'number', format: (value) => value, optional: true },
                    },
                    specialOptions: [
                        { value: 'all', label: 'asset.general.all_assets_option', dataField: 'all_assets' },
                    ],
                    addNewItem: {
                        method: 'modal-form',
                        modalForm: <AssetForm />,
                        linkedItem: (updatedData) => updatedData?.work_location?.id ? { location: updatedData.work_location.id } : undefined,
                    },
                },
                {
                    type: 'searchselect',
                    name: 'location_person',
                    label: 'contract.general.location_person_label',
                    placeholder: 'contract.general.location_person_placeholder',
                    objectName: 'person.general.object_name.singular',
                    query: { endpoint: 'get_person_search' },
                    showOptionsOnClick: (updatedData) => {
                        if (!updatedData.client?.id) return null;
                        return { endpoint: `get_person_search/${updatedData.client.id}` };
                    },
                    selectionFormat: 'name',
                    optionFormat: { title: { field: 'name', format: (value) => value } },
                    disableAddNew: true,
                    dataCondition: [
                        { field: 'client.entity_type', notValues: ['person'] },
                    ],
                    directOptionsTitle: 'job.general.persons_of_client_label',
                },
            ]
        },
        {
            component: 'ContractAssetsCard',
        },
        {
            component: 'ContractRevenueCard',
            dataCondition: [{ field: 'invoice_schedule', notEmpty: true }],
        },
    ];

    return (
        <DetailPage
            apiObject={apiObject}
            pageObject={pageObject}
            headerTitleDataField={headerTitleDataField}
            headerButtons={headerButtons}
            detailCards={detailCards}
            tabs={tabs}
        />
    );
};

export default ContractDetail;
